import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { HelpCircle, PlusIcon } from "lucide-react";
import { useAuditPlansQuery } from "../api/queries";
import {
  AuditPlanListTour,
  useAuditPlanListTour,
} from "../tour/AuditPlanListTour";
import { AuditPlanFilter } from "../ui/AuditPlanFilter";
import { AuditPlanTable } from "../ui/AuditPlanTable";

export function AuditPlanListPage() {
  const nav = useNavigate({ from: "/app/internal-audit" });
  const navToOther = useNavigate();
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/internal-audit" });
  const tour = useAuditPlanListTour();

  const list = useAuditPlansQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["internal_audit:create"]);

  return (
    <>
      <Header />

      <Main>
        <AuditPlanListTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mb-4">
          <div
            className="flex items-center justify-between"
            data-tourid="plan-toolbar"
          >
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-bold text-2xl">ຜະແນການກວດສອບພາຍໃນ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground">
                ຈັດການຜະແນການກວດສອບພາຍໃນຂອງອົງກອນ.
              </p>
            </div>
            {canManage && (
              <Button onClick={() => nav({ to: "/app/internal-audit/new" })}>
                <PlusIcon className="h-4 w-4" />
                ສ້າງຜະແນການກວດສອບໃໝ່
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <AuditPlanFilter />

          <AuditPlanTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({
                search: {
                  offset,
                  limit,
                  sort: search.sort,
                  filters: search.filters,
                },
              })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  offset: search.offset ?? 0,
                  limit: search.limit ?? 20,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                  filters: search.filters,
                },
              })
            }
            onEdit={(plan) =>
              navToOther({
                to: "/app/internal-audit/$id/edit",
                params: { id: plan.id },
              })
            }
            onView={(plan) =>
              navToOther({
                to: "/app/internal-audit/$id",
                params: { id: plan.id },
              })
            }
            onCreateRecord={(plan) =>
              navToOther({
                to: "/app/internal-audit/records/new/$planId",
                params: { planId: plan.id },
              })
            }
          />
        </div>
      </Main>
    </>
  );
}
